/**
 * Zeus Board Selector — 3D carousel + lazy-loaded bento modals.
 */
(function () {
  if (customElements.get("zeus-board-selector")) return;

  const RECO_STORAGE_KEY = "zeus-board-reco";
  const RECO_EVENT = "zeus:board-reco";

  function extractSectionId(element) {
    const section = element.classList?.contains("shopify-section") ? element : element.closest(".shopify-section");
    return section ? section.id.replace("shopify-section-", "") : null;
  }

  function pad2(n) {
    return String(n).padStart(2, "0");
  }

  class ZeusBoardSelector extends HTMLElement {
    #abort = new AbortController();
    #listeners = new AbortController();
    #raf = 0;
    #cards = [];
    #n = 0;
    #pos = -2.5;
    #target = 0;
    #vel = 0;
    #dragging = null;
    #tiltX = 0;
    #tiltY = 0;
    #tiltTX = 0;
    #tiltTY = 0;
    #lastActive = -1;
    #intro = 0;
    #modalIndex = 0;
    #modalOpen = false;
    #lastFocus = null;
    #declineFocus = null;
    #prevBodyOverflow = "";
    #bentoSectionId = null;
    #bentoCache = new Map();
    #bentoLoading = new Map();
    #wiredBentos = new WeakSet();
    #reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    #stiff = 0.07;
    #damp = 0.78;

    #stormTimers = [];
    #stormDone = false;
    #chargeCleanup = new Map();
    #recoApplied = false;
    #onReco = null;

    connectedCallback() {
      if (!this.dataset.bselReady) return;
      this.#stiff = this.#reduced ? 0.6 : 0.07;
      this.#damp = this.#reduced ? 0.2 : 0.78;
      this.#init();
      document.addEventListener("shopify:section:load", this.#onSectionLoad, {
        signal: this.#abort.signal,
      });
      this.#bindReco();
    }

    disconnectedCallback() {
      this.#stopTick();
      this.#abort.abort();
      this.#listeners.abort();
      if (this.#onReco) {
        document.removeEventListener(RECO_EVENT, this.#onReco);
        this.#onReco = null;
      }
    }

    #stopTick() {
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }

    #wakeTick() {
      if (this.#raf) return;
      this.#raf = requestAnimationFrame(() => this.#tick());
    }

    #onSectionLoad = (event) => {
      if (event.target.contains(this)) this.#init();
    };

    #init() {
      this.#stopTick();
      this.#listeners.abort();
      this.#listeners = new AbortController();
      this.#cards = Array.from(this.querySelectorAll("[data-bsel-card]"));
      this.#n = this.#cards.length;
      if (!this.#n) return;

      this.#stormDone = false;
      this.#recoApplied = false;
      this.#stormTimers.forEach((id) => window.clearTimeout(id));
      this.#stormTimers = [];
      this.#bentoCache.clear();
      this.#bentoLoading.clear();
      this.querySelector("[data-bsel-bento-host]")
        ?.querySelectorAll("[data-bsel-bento]")
        .forEach((el) => {
          el.remove();
        });

      this.#bentoSectionId = this.#resolveBentoSectionId();
      this.#pos = this.#reduced ? 0 : -2.5;
      this.#target = 0;
      this.#vel = 0;
      this.#lastActive = -1;
      this.#intro = 0;
      this.classList.remove("is-loaded");
      this.classList.add("is-ready");
      this.querySelector("[data-bsel-reco-banner]")?.setAttribute("hidden", "");
      this.querySelectorAll("[data-bsel-card]").forEach((card) => {
        card.classList.remove("is-reco-default", "is-reco-alt");
        delete card.dataset.bselRecoRole;
        card.querySelector("[data-bsel-reco-tag]")?.remove();
      });

      const signal = this.#listeners.signal;
      this.#bindCarousel(signal);
      this.#bindModal(signal);
      this.#bindDecline(signal);
      this.#bindKeyboard(signal);

      if (!this.#bindStorm(() => this.#finishIntro())) {
        this.#finishIntro();
      }

      this.#tryApplyStoredReco();
    }

    #finishIntro() {
      if (!this.#reduced) {
        requestAnimationFrame(() => this.classList.add("is-loaded"));
      } else {
        this.#intro = 1;
        this.classList.add("is-loaded");
      }

      this.#updateUI(0);
      this.#lastActive = 0;
      this.#wakeTick();
      this.#showDragHint();
    }

    #bindStorm(onDone) {
      const storm = this.querySelector("[data-bsel-storm]");
      if (!storm) return false;

      const mode = this.dataset.stormMode || "once_per_session";
      const storageKey = "zeus-bsel-storm-seen";

      if (this.#reduced || mode === "off") {
        storm.remove();
        return false;
      }

      if (mode === "once_per_session") {
        try {
          if (sessionStorage.getItem(storageKey)) {
            storm.remove();
            return false;
          }
        } catch (e) {
          storm.remove();
          return false;
        }
      }

      const skip = () => {
        if (this.#stormDone) return;
        this.#stormDone = true;
        this.#stormTimers.forEach((id) => window.clearTimeout(id));
        try {
          sessionStorage.setItem(storageKey, "1");
        } catch (e) {}
        storm.classList.add("is-out");
        this.#stormTimers.push(
          window.setTimeout(() => {
            storm.remove();
            onDone();
          }, 340),
        );
      };

      this.#stormTimers.push(window.setTimeout(() => storm.classList.add("is-s1"), 500));
      this.#stormTimers.push(window.setTimeout(() => storm.classList.add("is-s2"), 1100));
      this.#stormTimers.push(window.setTimeout(() => storm.classList.add("is-pre"), 1560));
      this.#stormTimers.push(
        window.setTimeout(() => {
          storm.classList.add("is-s3");
          storm.classList.remove("is-pre");
        }, 1700),
      );
      this.#stormTimers.push(window.setTimeout(skip, 2400));

      storm.addEventListener("pointerdown", skip, { once: true, signal: this.#listeners.signal });
      storm.addEventListener(
        "keydown",
        (e) => {
          if (!this.#stormDone && (e.key === "Escape" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            skip();
          }
        },
        { once: true, signal: this.#listeners.signal },
      );

      return true;
    }

    #resolveBentoSectionId() {
      const hostSection = this.closest(".shopify-section");
      let sibling = hostSection?.nextElementSibling || null;

      while (sibling) {
        const marker = sibling.matches?.("[data-bsel-bento-api-section-id]")
          ? sibling
          : sibling.querySelector?.("[data-bsel-bento-api-section-id]");
        if (marker) return marker.getAttribute("data-bsel-bento-api-section-id");

        if (sibling.classList?.contains("zeus-bsel-bento-partial-section")) {
          return extractSectionId(sibling);
        }

        sibling = sibling.nextElementSibling;
      }

      const marker = document.querySelector("[data-bsel-bento-api-section-id]");
      if (marker) return marker.getAttribute("data-bsel-bento-api-section-id");

      const partial = document.querySelector(".zeus-bsel-bento-partial-section");
      if (partial) return extractSectionId(partial);

      return "zeus-bsel-bento-partial";
    }

    #wrap(raw) {
      return (((raw % this.#n) + this.#n * 1.5) % this.#n) - this.#n / 2;
    }

    #tick() {
      if (this.classList.contains("is-loaded")) {
        this.#intro += (1 - this.#intro) * (this.#reduced ? 1 : 0.045);
      }

      if (!this.#dragging) {
        const force = (this.#target - this.#pos) * this.#stiff;
        this.#vel = (this.#vel + force) * this.#damp;
        this.#pos += this.#vel;
        if (Math.abs(this.#target - this.#pos) < 0.001 && Math.abs(this.#vel) < 0.001) {
          this.#pos = this.#target;
          this.#vel = 0;
        }
      }

      this.#tiltX += (this.#tiltTX - this.#tiltX) * 0.08;
      this.#tiltY += (this.#tiltTY - this.#tiltY) * 0.08;

      const act = ((Math.round(this.#pos) % this.#n) + this.#n) % this.#n;
      if (act !== this.#lastActive) {
        const shouldCharge = this.#lastActive >= 0;
        this.#lastActive = act;
        this.#updateUI(act);
        if (shouldCharge) this.#triggerCardCharge(act);
      }

      this.#renderCards();

      if (this.#needsFrame()) {
        this.#raf = requestAnimationFrame(() => this.#tick());
      } else {
        this.#raf = 0;
      }
    }

    #needsFrame() {
      return (
        !this.#modalOpen &&
        (this.#dragging ||
          Math.abs(this.#target - this.#pos) > 0.001 ||
          Math.abs(this.#vel) > 0.001 ||
          this.#intro < 0.999)
      );
    }

    #renderCards() {
      this.#cards.forEach((el, i) => {
        const o = this.#wrap(i - this.#pos);
        const a = Math.abs(o);
        const center = Math.max(0, 1 - a);
        const x = o * 282;
        const z = -a * 348;
        const ry = Math.max(-1, Math.min(1, o)) * -42 + this.#tiltY * center;
        const rx = this.#tiltX * center;
        const s = 1 - Math.min(a, 2) * 0.07;

        el.style.transform = `translateX(${x}px) translateZ(${z}px) rotateY(${ry}deg) rotateX(${rx}deg) scale(${s})`;
        el.style.opacity = String(Math.max(0, Math.min(1, 1 - (a - 0.2) * 0.25)) * this.#intro);
        el.style.filter = "none";
        el.style.zIndex = String(100 - Math.round(a * 10));
        el.classList.toggle("is-active", a < 0.5);

        const sheen = el.querySelector(".sheen");
        if (sheen) sheen.style.setProperty("--sx", `${50 - o * 60 + this.#tiltY * 2}%`);
      });

      const fill = this.querySelector("[data-bsel-fill]");
      if (fill && this.#n) {
        const frac = ((this.#pos % this.#n) + this.#n) % this.#n;
        fill.style.transform = `translateX(${frac * 100}%)`;
      }
    }

    #updateUI(act) {
      const card = this.#cards[act];
      if (!card) return;

      const panel = this.querySelector("[data-bsel-panel]");
      panel?.classList.add("is-swapping");
      window.setTimeout(() => panel?.classList.remove("is-swapping"), 200);

      const lengthEl = panel?.querySelector("[data-bsel-length]");
      const priceEl = panel?.querySelector("[data-bsel-price-panel]");
      if (lengthEl) lengthEl.textContent = card.dataset.bselLength || "—";
      if (priceEl) priceEl.textContent = card.dataset.bselPrice || "—";

      const cur = this.querySelector("[data-bsel-cur]");
      if (cur) cur.textContent = pad2(act + 1);

      const ambient = this.querySelector("[data-bsel-ambient]");
      if (ambient && card.dataset.bselGlow) {
        ambient.style.setProperty("--glow", card.dataset.bselGlow);
      }

      this.#cards.forEach((el, i) => {
        const peek = el.querySelector("[data-bsel-open]");
        if (peek) peek.tabIndex = i === act ? 0 : -1;
      });
    }

    #triggerCardCharge(index) {
      if (this.#reduced) return;
      const card = this.#cards[index];
      if (!card) return;

      this.#chargeCleanup.get(card)?.();
      card.classList.remove("is-charged", "is-strike");
      requestAnimationFrame(() => {
        card.classList.add("is-charged", "is-strike");
      });

      let timeout = 0;
      const signal = this.#listeners.signal;
      const cleanup = () => {
        window.clearTimeout(timeout);
        card.classList.remove("is-charged", "is-strike");
        signal.removeEventListener("abort", cleanup);
        this.#chargeCleanup.delete(card);
      };

      timeout = window.setTimeout(cleanup, 700);
      signal.addEventListener("abort", cleanup, { once: true });
      this.#chargeCleanup.set(card, cleanup);
    }

    #go(dir) {
      this.#target += dir;
      this.#dismissHint();
      this.#wakeTick();
    }

    #bindCarousel(signal) {
      const stage = this.querySelector("[data-bsel-stage]");
      if (!stage) return;

      stage.addEventListener(
        "pointerdown",
        (e) => {
          this.#dragging = {
            startX: e.clientX,
            startPos: this.#pos,
            lastX: e.clientX,
            lastT: performance.now(),
            v: 0,
          };
          this.#vel = 0;
          stage.classList.add("is-dragging");
          stage.setPointerCapture(e.pointerId);
          this.#dismissHint();
          this.#wakeTick();
        },
        { signal },
      );

      stage.addEventListener(
        "pointermove",
        (e) => {
          if (this.#dragging) {
            const now = performance.now();
            this.#dragging.v = (e.clientX - this.#dragging.lastX) / Math.max(1, now - this.#dragging.lastT);
            this.#dragging.lastX = e.clientX;
            this.#dragging.lastT = now;
            this.#pos = this.#dragging.startPos - (e.clientX - this.#dragging.startX) / 372;
          } else {
            const r = stage.getBoundingClientRect();
            this.#tiltTY = ((e.clientX - r.left) / r.width - 0.5) * 7;
            this.#tiltTX = -((e.clientY - r.top) / r.height - 0.5) * 5;
            this.#wakeTick();
          }
        },
        { signal },
      );

      stage.addEventListener(
        "pointerleave",
        () => {
          this.#tiltTX = 0;
          this.#tiltTY = 0;
        },
        { signal },
      );

      stage.addEventListener(
        "pointerup",
        (e) => {
          if (!this.#dragging) return;
          stage.classList.remove("is-dragging");
          const moved = Math.abs(e.clientX - this.#dragging.startX);
          if (moved < 6) {
            const hit = e.target.closest("[data-bsel-card]");
            if (hit && !hit.classList.contains("is-active")) {
              const i = this.#cards.indexOf(hit);
              this.#target = Math.round(this.#pos) + this.#wrap(i - Math.round(this.#pos));
            }
          } else {
            const momentum = -this.#dragging.v * 14;
            this.#target = Math.round(this.#pos + momentum * 0.35);
            this.#vel = momentum * 0.04;
          }
          this.#dragging = null;
        },
        { signal },
      );

      const clearDrag = () => {
        this.#dragging = null;
        stage.classList.remove("is-dragging");
      };
      stage.addEventListener("pointercancel", clearDrag, { signal });
      stage.addEventListener("lostpointercapture", clearDrag, { signal });

      this.querySelector("[data-bsel-prev]")?.addEventListener("click", () => this.#go(-1), { signal });
      this.querySelector("[data-bsel-next]")?.addEventListener("click", () => this.#go(1), { signal });

      this.#cards.forEach((card) => this.#bindCardOpen(card, signal));
    }

    #bindCardOpen(card, signal) {
      const peek = card.querySelector("[data-bsel-open]");
      if (!peek) return;
      peek.addEventListener("pointerdown", (e) => e.stopPropagation(), { signal });
      peek.addEventListener(
        "click",
        () => {
          const index = this.#cards.indexOf(card);
          if (index >= 0) this.#openModal(index);
        },
        { signal },
      );
    }

    async #loadBento(index) {
      const card = this.#cards[index];
      const cacheKey = String(card?.dataset.bselProductHandle || "")
        .trim()
        .toLowerCase();
      if (this.#bentoCache.has(cacheKey)) return this.#bentoCache.get(cacheKey);
      if (this.#bentoLoading.has(cacheKey)) return this.#bentoLoading.get(cacheKey);

      const handle = card?.dataset.bselProductHandle;
      const productUrl = card?.dataset.bselProductUrl;
      if (!cacheKey || !handle || !productUrl) throw new Error("Missing product handle");

      const promise = (async () => {
        const url = `${productUrl}?section_id=${this.#bentoSectionId}`;
        const response = await fetch(url, { priority: "low" });
        if (!response.ok) throw new Error(`Bento fetch failed: ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const panel = doc.querySelector("[data-bsel-bento]");
        if (!panel) throw new Error("Bento panel missing in response");

        panel.dataset.bselBentoIndex = String(index);
        panel.hidden = true;
        this.#bentoCache.set(cacheKey, panel);
        this.#wireBento(panel, index);
        this.#bentoLoading.delete(cacheKey);
        return panel;
      })();

      this.#bentoLoading.set(cacheKey, promise);
      promise.catch(() => this.#bentoLoading.delete(cacheKey));
      return promise;
    }

    #prefetchBento(index) {
      if (index < 0 || index >= this.#n) return;
      this.#loadBento(index).catch(() => {});
    }

    async #showBento(index) {
      const host = this.querySelector("[data-bsel-bento-host]");
      const loading = this.querySelector("[data-bsel-bento-loading]");
      if (!host) return;

      const card = this.#cards[index];
      const cacheKey = String(card?.dataset.bselProductHandle || "")
        .trim()
        .toLowerCase();
      const cached = cacheKey ? this.#bentoCache.get(cacheKey) : null;
      host.classList.remove("is-error");

      if (cached) {
        host.classList.remove("is-loading");
        if (loading) loading.hidden = true;
      } else {
        host.classList.add("is-loading");
        if (loading) {
          loading.hidden = false;
          const text = loading.querySelector(".zeus-bsel__bento-loading-text");
          if (text) {
            text.textContent = this.dataset.bselBentoLoading || text.dataset.defaultText || text.textContent;
          }
        }
      }

      try {
        const panel = cached || (await this.#loadBento(index));
        host.querySelectorAll("[data-bsel-bento]").forEach((el) => {
          el.hidden = true;
        });
        if (!host.contains(panel)) host.appendChild(panel);
        panel.hidden = false;
        host.classList.remove("is-loading");
        if (loading) loading.hidden = true;
        host.classList.add("is-swap");
        requestAnimationFrame(() => host.classList.remove("is-swap"));

        const mcount = this.querySelector("[data-bsel-mcount]");
        if (mcount) mcount.textContent = `${pad2(index + 1)} / ${pad2(this.#n)}`;

        this.#animatePerfBars(panel);
        this.#prefetchBento((index + 1) % this.#n);
        this.#prefetchBento((index - 1 + this.#n) % this.#n);
      } catch (err) {
        console.warn("[zeus-board-selector] bento load failed", err);
        host.classList.remove("is-loading");
        host.classList.add("is-error");
        if (loading) {
          loading.hidden = false;
          const text = loading.querySelector(".zeus-bsel__bento-loading-text");
          if (text) text.textContent = this.dataset.bselBentoError || "Could not load this board.";
        }
      }
    }

    #wireBento(panel, index) {
      if (this.#wiredBentos.has(panel)) return;
      this.#wiredBentos.add(panel);

      const declineBtn = panel.querySelector("[data-bsel-decline-open]");
      if (declineBtn && this.dataset.declineEnabled !== "false") {
        declineBtn.addEventListener("click", () => this.#openDecline(panel));
      }

      const form = panel.querySelector("[data-bsel-form]");
      if (form) {
        form.addEventListener("submit", (e) => this.#onAtcSubmit(e, form));
      }

      form?.addEventListener("variant:change", (e) => this.#onVariantChange(panel, e));
    }

    #openDecline(panel) {
      const overlay = this.querySelector("[data-bsel-decline-overlay]");
      if (!overlay) return;

      const tag = overlay.querySelector("[data-bsel-decline-tag]");
      const title = overlay.querySelector("[data-bsel-decline-title]");
      const sub = overlay.querySelector("[data-bsel-decline-sub]");
      const body = overlay.querySelector("[data-bsel-decline-body]");
      const template = panel.querySelector("[data-decline-body-template]");

      if (tag) tag.textContent = panel.dataset.declineTag || "";
      if (title) title.textContent = panel.dataset.declineTitle || "";
      if (sub) sub.textContent = panel.dataset.declineSub || "";
      if (body && template) body.innerHTML = template.innerHTML;

      this.#lockScroll();
      overlay.classList.add("is-open");
      overlay.setAttribute("aria-hidden", "false");
      this.#declineFocus = document.activeElement;
      overlay.querySelector("[data-bsel-decline-stay]")?.focus();
    }

    #closeDecline() {
      const overlay = this.querySelector("[data-bsel-decline-overlay]");
      if (!overlay) return;
      overlay.classList.remove("is-open");
      overlay.setAttribute("aria-hidden", "true");
      this.#unlockScroll();
      if (this.#declineFocus) {
        this.#declineFocus.focus();
        this.#declineFocus = null;
      }
    }

    #bindDecline(signal) {
      this.querySelector("[data-bsel-decline-stay]")?.addEventListener("click", () => this.#closeDecline(), {
        signal,
      });
      this.querySelector("[data-bsel-decline-leave]")?.addEventListener(
        "click",
        () => {
          const btn = this.querySelector("[data-bsel-decline-leave]");
          if (btn) btn.textContent = this.dataset.declineLeaveDone || "See you soon.";
          window.setTimeout(() => this.#closeDecline(), 1100);
        },
        { signal },
      );
      this.querySelector("[data-bsel-decline-overlay]")?.addEventListener(
        "click",
        (e) => {
          if (e.target.matches("[data-bsel-decline-overlay]")) this.#closeDecline();
        },
        { signal },
      );
    }

    async #onAtcSubmit(event, form) {
      event.preventDefault();
      const atc = form.querySelector("[data-bsel-atc]");
      const variantId = form.querySelector("[data-bsel-variant-id]");
      if (!variantId?.value) return;

      if (atc) {
        atc.disabled = true;
        atc.setAttribute("data-loading", "1");
      }

      try {
        const fd = new FormData(form);
        const cartUrl = window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : window.routes?.cart_add_url
            ? `${window.routes.cart_add_url}.js`
            : "/cart/add.js";

        const res = await fetch(cartUrl, {
          method: "POST",
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json();
          throw err;
        }

        if (atc) {
          const success = atc.dataset.atcSuccess;
          if (success) {
            const original = atc.textContent;
            atc.textContent = success;
            window.setTimeout(() => {
              atc.textContent = original;
            }, 1600);
          }
        }

        this.#closeModal();
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        document.querySelector("cart-drawer")?.show?.();
      } catch (err) {
        console.warn("[zeus-board-selector] ATC failed", err);
        const msg = err?.description || err?.message || this.dataset.bselAtcError || "Could not add to cart.";
        window.alert(msg);
      } finally {
        if (atc) {
          atc.disabled = false;
          atc.removeAttribute("data-loading");
        }
      }
    }

    #onVariantChange(panel, event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      const form = panel.querySelector("[data-bsel-form]");
      const variantInput = form?.querySelector("[data-bsel-variant-id]");
      const atc = form?.querySelector("[data-bsel-atc]");
      const bisWrap = form?.querySelector("[data-bsel-bis-wrap]");
      const priceEl = panel.querySelector("[data-bsel-price]");

      if (variantInput) variantInput.value = variant.id;
      if (priceEl && variant.price != null) {
        const money = window.Shopify?.formatMoney ? window.Shopify.formatMoney(variant.price) : String(variant.price);
        priceEl.textContent = money;
      }

      const available = variant.available;
      if (atc) atc.hidden = !available;
      if (bisWrap) bisWrap.hidden = available;
    }

    #animatePerfBars(panel) {
      panel.querySelectorAll(".zeus-bsel__tile.perf .rf, .tile.perf .rf").forEach((bar) => {
        bar.style.width = "0";
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.querySelectorAll(".zeus-bsel__tile.perf .rf, .tile.perf .rf").forEach((bar) => {
            bar.style.width = "";
          });
        });
      });
    }

    #bindModal(signal) {
      const modal = this.querySelector("[data-bsel-modal]");
      modal?.querySelector("[data-bsel-backdrop]")?.addEventListener("click", () => this.#closeModal(), {
        signal,
      });
      modal?.querySelector("[data-bsel-mclose]")?.addEventListener("click", () => this.#closeModal(), {
        signal,
      });
      modal?.querySelector("[data-bsel-mprev]")?.addEventListener(
        "click",
        () => {
          if (!modal.classList.contains("is-open")) return;
          this.#modalGo(-1);
        },
        { signal },
      );
      modal?.querySelector("[data-bsel-mnext]")?.addEventListener(
        "click",
        () => {
          if (!modal.classList.contains("is-open")) return;
          this.#modalGo(1);
        },
        { signal },
      );
    }

    #lockScroll() {
      if (document.body.style.overflow !== "hidden") this.#prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    #unlockScroll() {
      if (this.querySelector("[data-bsel-modal].is-open") || this.querySelector("[data-bsel-decline-overlay].is-open"))
        return;
      document.body.style.overflow = this.#prevBodyOverflow;
    }

    #focusables(root) {
      return Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el.getClientRects().length > 0);
    }

    #trapFocus(e, root) {
      if (e.key !== "Tab") return;
      const items = this.#focusables(root);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    async #openModal(index) {
      this.#modalIndex = index;
      this.#modalOpen = true;
      const modal = this.querySelector("[data-bsel-modal]");
      if (!modal) return;

      this.#lockScroll();
      modal.classList.add("is-open");
      modal.setAttribute("aria-hidden", "false");
      this.#lastFocus = document.activeElement;
      this.#updateModalHeading(index);
      modal.querySelector("[data-bsel-mclose]")?.focus();
      this.#stopTick();
      await this.#showBento(index);
    }

    async #modalGo(dir) {
      this.#modalIndex = (this.#modalIndex + dir + this.#n) % this.#n;
      this.#updateModalHeading(this.#modalIndex);
      this.#triggerCardCharge(this.#modalIndex);
      await this.#showBento(this.#modalIndex);
      this.#target = Math.round(this.#pos) + this.#wrap(this.#modalIndex - Math.round(this.#pos));
    }

    #updateModalHeading(index) {
      const title = this.querySelector("[data-bsel-modal] [id^='bsel-modal-title-']");
      const cardTitle = this.#cards[index]?.querySelector("h2")?.textContent?.trim();
      if (title && cardTitle) title.textContent = cardTitle;
    }

    #closeModal() {
      this.#modalOpen = false;
      const modal = this.querySelector("[data-bsel-modal]");
      if (!modal) return;
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      this.#unlockScroll();
      if (this.#lastFocus) {
        this.#lastFocus.focus();
        this.#lastFocus = null;
      }
      this.#wakeTick();
    }

    #bindKeyboard(signal) {
      document.addEventListener(
        "keydown",
        (e) => {
          const decline = this.querySelector("[data-bsel-decline-overlay].is-open");
          const modal = this.querySelector("[data-bsel-modal].is-open");

          if (e.key === "Escape") {
            if (decline) this.#closeDecline();
            else if (modal) this.#closeModal();
            return;
          }

          if (decline) {
            this.#trapFocus(e, decline.querySelector(".zeus-bsel__decline-card") || decline);
            return;
          }

          if (modal) {
            this.#trapFocus(e, modal.querySelector(".zeus-bsel__sheet") || modal);
            if (e.key === "ArrowLeft") this.#modalGo(-1);
            if (e.key === "ArrowRight") this.#modalGo(1);
            return;
          }

          const activeElement = document.activeElement;
          const isEditable = activeElement?.matches("input, select, textarea, [contenteditable='true']");
          if (!this.contains(activeElement) || isEditable) return;

          if (e.key === "ArrowLeft") this.#go(-1);
          if (e.key === "ArrowRight") this.#go(1);
          if (e.key === "Enter" || e.key === " ") {
            const active = this.#cards[this.#lastActive];
            if (active?.classList.contains("is-active") && activeElement?.closest("[data-bsel-card]")) {
              e.preventDefault();
              this.#openModal(this.#lastActive);
            }
          }
        },
        { signal },
      );
    }

    #dismissHint() {
      this.querySelector("[data-bsel-drag-hint]")?.classList.add("is-gone");
    }

    #showDragHint() {
      if (this.#reduced) return;
      const hint = this.querySelector("[data-bsel-drag-hint]");
      if (!hint) return;
      hint.classList.remove("is-gone");
      window.setTimeout(() => hint.classList.add("is-show"), 600);
      window.setTimeout(() => this.#dismissHint(), 7600);
    }

    #readStoredReco() {
      try {
        const raw = sessionStorage.getItem(RECO_STORAGE_KEY);
        if (!raw) return null;
        const payload = JSON.parse(raw || "{}");
        if (!payload || !Array.isArray(payload.handles) || !payload.handles.length) return null;
        return payload;
      } catch (e) {
        return null;
      }
    }

    #bindReco() {
      this.#onReco = (event) => {
        this.#applyReco(event.detail);
      };
      document.addEventListener(RECO_EVENT, this.#onReco);
    }

    #tryApplyStoredReco() {
      const payload = this.#readStoredReco();
      if (payload) this.#applyReco(payload);
    }

    #normalizeHandles(handles) {
      return [
        ...new Set(
          handles
            .map((handle) =>
              String(handle || "")
                .trim()
                .toLowerCase(),
            )
            .filter(Boolean),
        ),
      ].slice(0, 2);
    }

    #promoteReservedCards(handles) {
      const reserve = this.querySelector("[data-bsel-reco-reserve]");
      const stage = this.querySelector("[data-bsel-stage]");
      if (!reserve?.content || !stage) return;

      const wanted = new Set(handles.filter(Boolean));
      const rendered = new Set(
        this.#cards
          .map((card) =>
            String(card.dataset.bselProductHandle || "")
              .trim()
              .toLowerCase(),
          )
          .filter(Boolean),
      );

      reserve.content.querySelectorAll("[data-bsel-card]").forEach((card) => {
        const handle = String(card.dataset.bselProductHandle || "")
          .trim()
          .toLowerCase();
        if (!handle || !wanted.has(handle) || rendered.has(handle)) return;

        const promoted = card.cloneNode(true);
        stage.appendChild(promoted);
        this.#bindCardOpen(promoted, this.#listeners.signal);
        rendered.add(handle);
      });

      this.#cards = Array.from(this.querySelectorAll("[data-bsel-card]"));
      this.#n = this.#cards.length;
    }

    #centerRecommendation() {
      this.#target = 0;
      this.#pos = 0;
      this.#vel = 0;
      this.#lastActive = -1;
      this.#recoApplied = true;
      if (this.classList.contains("is-loaded")) {
        this.#updateUI(0);
        this.#lastActive = 0;
        this.#wakeTick();
      }
    }

    #applyReco(payload) {
      if (!payload || !Array.isArray(payload.handles) || !this.#n) return;

      const stage = this.querySelector("[data-bsel-stage]");
      if (!stage) return;

      const handles = this.#normalizeHandles(payload.handles);
      const banner = this.querySelector("[data-bsel-reco-banner]");

      this.querySelectorAll("[data-bsel-card]").forEach((card) => {
        card.classList.remove("is-reco-default", "is-reco-alt");
        delete card.dataset.bselRecoRole;
        card.querySelector("[data-bsel-reco-tag]")?.remove();
      });
      if (!handles.length) {
        if (banner) banner.hidden = true;
        return;
      }

      this.#promoteReservedCards(handles);

      const roles = new Map();
      handles.forEach((handle, index) => {
        if (handle) roles.set(handle, index === 0 ? "default" : "alternative");
      });
      if (!roles.size) return;

      const recoCards = [];
      const otherCards = [];
      this.#cards.forEach((card) => {
        const handle = String(card.dataset.bselProductHandle || "")
          .trim()
          .toLowerCase();
        if (roles.has(handle)) {
          recoCards.push({ card, order: handles.indexOf(handle), role: roles.get(handle) });
        } else {
          otherCards.push(card);
        }
      });
      if (!recoCards.length) return;

      recoCards.sort((a, b) => a.order - b.order);
      [...recoCards.map((entry) => entry.card), ...otherCards].forEach((card) => stage.appendChild(card));

      const tagDefault = this.dataset.recoTagDefault || "Mon choix";
      const tagAlt = this.dataset.recoTagAlternative || "Si tu hésites";

      recoCards.forEach(({ card, role }) => {
        card.classList.remove("is-reco-default", "is-reco-alt");
        card.classList.add(role === "default" ? "is-reco-default" : "is-reco-alt");
        card.dataset.bselRecoRole = role;

        let tag = card.querySelector("[data-bsel-reco-tag]");
        if (!tag) {
          tag = document.createElement("span");
          tag.className = "zeus-bsel__reco-tag";
          tag.setAttribute("data-bsel-reco-tag", "");
          const anchor = card.querySelector(".top") || card;
          anchor.insertBefore(tag, anchor.firstChild);
        }
        tag.textContent = role === "default" ? tagDefault : tagAlt;
        tag.hidden = false;
      });

      this.#cards = Array.from(this.querySelectorAll("[data-bsel-card]"));
      this.#n = this.#cards.length;

      const totalEl = this.querySelector("[data-bsel-total]");
      if (totalEl) totalEl.textContent = pad2(this.#n);

      if (banner) {
        banner.hidden = false;
        const volEl = banner.querySelector("[data-bsel-reco-vol]");
        if (volEl) volEl.textContent = payload.targetVol != null ? String(payload.targetVol) : "";
      }

      this.#centerRecommendation();
    }
  }

  customElements.define("zeus-board-selector", ZeusBoardSelector);
})();
